<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useDialog } from 'primevue/usedialog'
import {
  MismatchingState,
  MismatchingStateType,
  RestaurantAreas,
} from '~/lib/interfaces/restaurant'
import { EcommerceProductData } from '~/lib/models/async-data'
import { McdViewError } from '~/lib/models/errors'
import { GTMContentGroup } from '~/lib/models/gtm'
import { ProductEcommerce } from '~/lib/services/store/catalog/catalog.dtos'
import { ProductDetailPromotion } from '~/lib/services/store/common.dto'

definePageMeta({
  alias: [
    '/restaurantes/:citySlug/:restaurantSlug/pedidos/:areaSlug/:categorySlug/:productSlug',
    '/restaurants/:citySlug/:restaurantSlug/commandes/:areaSlug/:categorySlug/:productSlug',
  ],
  pageType: 'PRODUCT_DETAIL',
})

const route = useRoute()
const restaurantStore = useRestaurantStore()
const appStore = useAppStore()
const { initialFetchLoaded } = storeToRefs(appStore)
const restaurantActions = useRestaurantActions()
const dialog = useDialog()
const { t } = useLocale()
const { trackPageView, trackViewItem } = useGTM()
const errorView = useErrorView()
const { catalogServiceRef } = useStoreService()
const catalogService = catalogServiceRef()

const { restaurantSlug, areaSlug, categorySlug, productSlug } = getRouteParams(route.params, [
  'restaurantSlug',
  'areaSlug',
  'categorySlug',
  'productSlug',
])
const restaurantCode = getRestaurantCodeFromSlug({
  country: appStore.countryCode,
  slug: restaurantSlug,
})

const productDetail = ref<ProductEcommerce>()
const freeShippingPromotion = ref<ProductDetailPromotion>()

const { data, pending, error } = await useWrapperKeyAsyncData<EcommerceProductData, McdViewError>(
  `ecommerce-area-category-product-${restaurantSlug}-${areaSlug}-${categorySlug}-${productSlug}`,
  () =>
    restaurantActions.getEcommerceAreaCategoryProductData(
      restaurantCode,
      areaSlug,
      categorySlug,
      productSlug
    ),
  {
    server: false,
    lazy: true,
    immediate: initialFetchLoaded.value,
    watch: [initialFetchLoaded],
  }
)

const changeSize = async (identifier: string) => {
  try {
    pending.value = true
    const restaurantId = data.value?.restaurant?.id || ''
    const area = data.value?.area || RestaurantAreas.MOP
    // We have category but product size can be on other category
    const categoryId = ''
    const fetchProductInput = {
      countryCode: appStore.countryCode,
      restaurantId,
      categoryId,
      productId: identifier,
      area,
    }

    const product = await catalogService.getProductDetail(fetchProductInput)
    productDetail.value = product
    await checkPromos()
  } catch (err: any) {
    // eslint-disable-next-line no-console
    console.log('🚀 ~ file: index.vue:50 ~ changeSize ~ error:', err)
    await showDialogAlert({
      dialog,
      data: {
        title: t('errors.alerts.generic.title'),
        body: t('errors.alerts.generic.body'),
        btnPrimary: t('errors.alerts.generic.accept'),
      },
    })
  } finally {
    pending.value = false
  }
}

const notMatchingStatus = computed<MismatchingStateType | undefined>(() => {
  if (!restaurantStore.isRestaurantSelected) return MismatchingState.NO_RESTAURANT_SELECTED
  const notMatchingRestaurant =
    restaurantStore.selectedRestaurant?.restaurant?.code !== data.value?.restaurant?.code
  const notMatchingArea = restaurantStore.areaSelected !== data.value?.area
  if (notMatchingArea && notMatchingRestaurant) return MismatchingState.MISMATCHING_AREA_RESTAURANT
  else if (notMatchingArea) return MismatchingState.MISMATCHING_AREA
  else if (notMatchingRestaurant) return MismatchingState.MISMATCHING_RESTAURANT
  else return undefined
})

const trackItemEvents = () => {
  if (productDetail.value) {
    trackPageView({
      cg: GTMContentGroup.primary.ECOMMERCE,
      cg2: GTMContentGroup.secondary.ITEM_DETAIL,
      cg3: productDetail.value.identifier,
    })
    const categoryTitle = data.value?.category.title
    trackViewItem(productDetail.value, categoryTitle)
  }
}

const checkPromos = async () => {
  if (!notMatchingStatus.value && productDetail.value?.promotions.length) {
    freeShippingPromotion.value = await checkFreeShippingPromo(productDetail.value.promotions)
  }
}

watch(error, () => {
  if (error.value) {
    // I am receiving here an H3Error not a McdViewError, maybe we can try with throw createError and built in nuxt error handling
    // for now is a workaround that works
    const cause = error.value.cause instanceof McdViewError ? error.value.cause : undefined
    errorView.value = cause || error.value
  }
})

watch(data, async () => {
  if (!error.value && data.value?.product) {
    productDetail.value = data.value?.product
    trackItemEvents()
    await checkPromos()
  }
})

useHeadSafe({
  meta: [
    {
      property: 'og:image',
      content: productDetail?.value?.imageUrl || '/images/icons/placeholder.svg',
    },
    {
      property: 'og:image:alt',
      content: productDetail?.value?.name,
    },
    {
      name: 'robots',
      content: 'noindex, nofollow',
    },
  ],
})
</script>

<template>
  <SkeletonProductDetail v-if="pending" />
  <LazyErrorView v-else-if="errorView" :error="errorView" />
  <section v-else-if="data && productDetail">
    <ProductViewEcommerce
      :product="productDetail"
      :category="data.category"
      :restaurant-info="data.restaurant"
      :area="data.area"
      :not-matching-status="notMatchingStatus"
      :free-shipping-promo="freeShippingPromotion"
      @on-change-size="changeSize"
    />
  </section>
</template>
